import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { TimeInMilliseconds } from 'src/Libs/types'
import { fetchDynamicTariffSettings } from '../api'
import { DynamicTariffSettingsModel } from '../model'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchDynamicTariffSettings {
  dynamicTariffSettings: DynamicTariffSettingsModel | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

const STALE_TIME = TimeInMilliseconds.oneHour
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond
const MAX_RETRY_ATTEMPTS = 3

/** retries after 0.5, 1, 2, 4, 8, ... , (seconds) */
const exponentialBackoffDelay = (attempt: number): number => Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)

/** react-query key for dynamic tariff settings */
export const getDynamicTariffSettingsQueryKey = (installationId: string | undefined): [string, string | undefined] => [
  'dynamic-tariff-settings',
  installationId,
]

/**
 * Hook for fetching dynamic tariff settings
 */
const useFetchDynamicTariffSettings = (installationId: string | undefined): UseFetchDynamicTariffSettings => {
  const queryKey = getDynamicTariffSettingsQueryKey(installationId)
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<DynamicTariffSettingsModel | null> => {
    const { error: fetchError, hasErrors, value } = await fetchDynamicTariffSettings(installationId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey,
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    staleTime: STALE_TIME,
    retryDelay: exponentialBackoffDelay,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  return { dynamicTariffSettings: data ?? null, loading: isLoading, hasError: isError }
}

export default useFetchDynamicTariffSettings
